import { RecordModel, AthletesModel, Exercise } from "./types.js";

/** Minimal record fields required to decide a record break and show its badge. */
export type RecordAttemptMark = Pick<
  RecordModel,
  | "poids"
  | "catAgeId"
  | "catPoidId"
  | "sexe"
  | "mouvement"
  | "disciplineId"
  | "recordBadgeUrl"
  | "recordLabel"
  | "organisationName"
  | "athleteName"
  | "catAge"
> & {
  /** Organisation owning this mark; used to segment league / national / world. */
  organisationId?: string | null;
};
/**
 * Compact age-category label for record tooltips (code preferred, e.g. SNR / JR).
 *
 * @param record - Mark that may include a joined `catAge` relation.
 * @returns Trimmed code or name, or null when neither is available.
 */
export function formatRecordAgeLabel(
  record: Pick<RecordAttemptMark, "catAge">,
): string | null {
  const code = record.catAge?.code?.trim();
  if (code) {
    return code;
  }
  const nom = record.catAge?.nom?.trim();
  return nom || null;
}
/**
 * Builds the tooltip title as `Record de France · SNR` (label preferred over org name).
 *
 * @param record - Mark with optional label / organisation / age category.
 * @param fallback - Localized fallback when neither label nor org name is set.
 * @returns Title with age suffix when available.
 */
export function formatRecordTitleWithAge(
  record: Pick<
    RecordAttemptMark,
    "recordLabel" | "organisationName" | "catAge"
  >,
  fallback: string,
): string {
  const name =
    record.recordLabel?.trim() || record.organisationName?.trim() || fallback;
  const ageLabel = formatRecordAgeLabel(record);
  return ageLabel ? `${name} · ${ageLabel}` : name;
}

/**
 * Result of comparing an attempt against matching federation marks.
 * Multiple organisations can be beaten at once (e.g. HDF + FFForce + IPF).
 */
export type RecordAttemptResult = {
  /** Highest matching mark weight across all orgs (threshold for the top level). */
  maxRecord: number;
  /** True when at least one organisation mark is beaten. */
  isRecord: boolean;
  /**
   * Highest beaten mark (legacy single-badge consumers).
   * Prefer `beatenRecords` when rendering multi-level badges.
   */
  beatenRecord: RecordAttemptMark | null;
  /** Every organisation mark beaten by the attempt (one entry per org). */
  beatenRecords: RecordAttemptMark[];
};

/** Synthetic key when a local row has no organisationId (legacy FFForce). */
const LEGACY_ORG_KEY = "__legacy__";

/**
 * Resolves the athlete age-category id used for record matching (PL alias).
 */
function resolveRecordCatAgeId(athlete: AthletesModel): number {
  if (athlete.discipline?.code == "PL" && athlete.catAgeId == 8) {
    return 9;
  }
  return athlete.catAgeId;
}

/**
 * Filters records that match the athlete category / sex / movement / discipline.
 */
function matchingRecordsForAttempt(
  records: RecordAttemptMark[],
  athlete: AthletesModel,
  movement: Exercise | "Total",
): RecordAttemptMark[] {
  const catAgeId = resolveRecordCatAgeId(athlete);
  const mouvement = movement === "Total" ? "Total" : movement.name;
  return records.filter(
    (record) =>
      record.catAgeId === catAgeId &&
      record.catPoidId === athlete.catPoidId &&
      record.sexe === athlete.sexe &&
      record.mouvement === mouvement &&
      (record.disciplineId == null ||
        record.disciplineId === athlete.disciplineId),
  );
}

/**
 * Keeps the highest mark per organisation (league / national / world).
 *
 * @param matching - Records already filtered for the athlete slot.
 * @returns One mark per organisationId (legacy rows share a synthetic key).
 */
export function highestMarkPerOrganisation(
  matching: RecordAttemptMark[],
): RecordAttemptMark[] {
  const byOrg = new Map<string, RecordAttemptMark>();
  for (const record of matching) {
    const key = record.organisationId?.trim()
      ? record.organisationId
      : LEGACY_ORG_KEY;
    const existing = byOrg.get(key);
    if (!existing || record.poids > existing.poids) {
      byOrg.set(key, record);
    }
  }
  return Array.from(byOrg.values());
}

/**
 * Checks if a lift attempt would be a new record at one or more organisation levels.
 *
 * @param value - The weight value of the attempt.
 * @param athlete - The athlete making the attempt.
 * @param records - Array of all records (may include cumulative org marks).
 * @param movement - The current movement/exercise.
 * @returns Whether any org mark is beaten, plus every beaten mark (distinct badges).
 */
export function isRecordAttempt(
  value: number | null | undefined,
  athlete: AthletesModel,
  records: RecordAttemptMark[],
  movement: Exercise | "Total",
): RecordAttemptResult {
  const empty: RecordAttemptResult = {
    maxRecord: 0,
    isRecord: false,
    beatenRecord: null,
    beatenRecords: [],
  };
  if (!value || !athlete) {
    return empty;
  }
  const matching = matchingRecordsForAttempt(records, athlete, movement);
  if (matching.length === 0) {
    return empty;
  }
  const perOrg = highestMarkPerOrganisation(matching);
  const maxRecord = perOrg.reduce(
    (max, record) => (record.poids > max ? record.poids : max),
    0,
  );
  // Include every org whose own mark is beaten (not only the global max).
  const beatenRecords = perOrg
    .filter((record) => value > record.poids)
    .sort((a, b) => b.poids - a.poids);
  if (beatenRecords.length === 0) {
    return {
      maxRecord,
      isRecord: false,
      beatenRecord: null,
      beatenRecords: [],
    };
  }
  return {
    maxRecord,
    isRecord: true,
    beatenRecord: beatenRecords[0] ?? null,
    beatenRecords,
  };
}
